import {makeStyles} from '@material-ui/core/styles';
import * as Yup from 'yup';
import {
    GetUAProfileMetaInfoResponse,
    OptionsTemplateExtras,
    OptionsTemplateInfo,
    PhoneDirectory,
    TemplateKeysOptionsInfo,
    UAProfileKeyAction,
    UAProfileKeyActionConfiguration,
    UAProfileKeyInfo,
    UATypeInfo
} from '../../store/types/Devices';
import {DeviceProfileDetailsActionKeyRowData} from './DeviceProfileDetailsActionKeyRow.utils';
import i18n from '../../services/i18n';
import {YesNo} from '../../store/types/CallScreening';
import {TFunction} from 'react-i18next';
import {v4 as id} from 'uuid';
import {DevicePhoneBookAssignItem} from './DevicePhoneBookTab.utils';
import {Colors} from '../../styles/Colors';

export type DeviceProfileDetailsTabProps = {
    dataQa?: string;
    className?: string;
    showAsciiValue?: boolean;
    uaProfileMetaInfo: GetUAProfileMetaInfoResponse | undefined;
    readonly?: boolean;
    onChangeTemplate?: (v: OptionsTemplateInfo) => void;
    handleOnSave?: (form: DeviceProfileDetailsTabFormType, ignoreOnSave: boolean) => void;
    iUaType?:number
};

export type DeviceProfileDetailsTabFormType = {
    i_ua_config_template: number | undefined;
    profileName: string;
    directories: DevicePhoneBookAssignItem[];
    softKeys: DeviceProfileDetailsActionKeyRowData[],
    lineKeys: DeviceProfileDetailsActionKeyRowData[],
    expansionKeys: DeviceProfileDetailsActionKeyRowData[],
};

export const useStyle = makeStyles(() => ({
    topContainer: {
        position: 'relative',
    },
    rootDeviceProfileDetailsTab: {
        position: 'relative',
    },
    itemsContainer: {
        '& .MuiFormControl-root': {
            width: '100%',
        },
    },

    dual: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        padding: 12,
        paddingTop: 0,
        '& .MuiFormControl-root': {
            maxWidth: 440,
        },
        '& div.container':{
            width:'100%'
        }
    },

    toEnd: {
        justifyContent: 'end',
        display: 'flex',
        width:'100%'
    },

    profileName: {
        width: '400px !important',
        maxWidth: '400px !important',
        height: 58,
        marginBottom: 22,
    },

    descriptionTooltipContainer: {
        position: 'absolute',
        marginTop: -35,
        marginLeft: 9,
        right: 32,
        left: 32,
        opacity: 0,
    },

    scrollInsideDual: {
        height: 'fit-content',
        width: 676,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        overflowX: 'hidden',
        overflowY: 'auto',
        marginBottom: 130,
        marginTop: 12,
    },

    sectionHeader: {
        width: 676,
        height: 40,
        minHeight: 40,
        marginBottom: 8,
        fontSize: 18,
        fontWeight: 700,
        lineHeight: '24px',
        textAlign: 'left'
    },

    keyPadContainer: {
        top: 12,
        height: 'calc(100vh - 120px - 49px - 40px - 12px)',
        position: 'sticky',
        display: 'flex',
        justifyContent: 'center',
        width: 'calc(100% - 676px)',
        flexDirection: 'row',
    },

    containerOfImageAndButtons: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column'
    },

    imageAndButtonsContainer: {
        position: 'relative'
    },

    phoneImageContainer: {
        userSelect: 'none',
    },

    profileDropdownContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
    },

    externalLinkeContainer: {
        paddingTop: 20,
        paddingLeft: 30,
        cursor: 'pointer',
    },
    
    loader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: 'calc(100vh - 320px)'
    },

    assignedDirectoryContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column',
        marginBottom: 16,
    },

    assignedDirectoryItem: {
        width: 397,
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        height: 64,
    },

    assignedDirectoryItemWithBorder: {
        borderTop: 'solid 1px rgba(221, 225, 231, 1)',
    },

    assignedDirectoryItemSwitch: {
        width: 58,
        paddingTop: 12,
    },

    assignedDirectoryItemName: {
        marginLeft: 16,
        width: 205,
        paddingTop: 18,

        fontWeight: 400,
        fontSize: 16,
        color: Colors.Text
    },

    assignedDirectoryItemItems: {
        marginLeft: 16,
        width: 64,
        paddingTop: 14,
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column'
    },

    assignedDirectoryItemItemsSubTop: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '20px',
        color: Colors.Text,
        textAlign: 'center'
    },

    assignedDirectoryItemItemsSubBottom: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: '20px',
        color: Colors.Text3,
        textAlign: 'center'
    },

    assignedDirectoryItemNavigate: {
        marginLeft: 27,
        width: 40,
        paddingTop: 22,
        cursor: 'pointer',
        color: 'rgba(0, 0, 0, 0.6)'
    }
}));

const requiredFieldError = i18n.t('errors:common.emptyInput');
const actionKeyValidator = Yup.object().shape({
    value: Yup.string().when('selectedActionConfiguration', {
        is: (selectedActionConfiguration: UAProfileKeyActionConfiguration[]) => 
            {
                const mandatory = selectedActionConfiguration
                    ?.find(e => e.attribute === 'value')
                    ?.mandatory ?? 0;
                return mandatory > 0;
            },
        then: Yup.string().required(requiredFieldError),
        otherwise: Yup.string().notRequired().nullable(),
    }),
    label: Yup.string().when('selectedActionConfiguration', {
        is: (selectedActionConfiguration: UAProfileKeyActionConfiguration[]) => 
            {
                const mandatory = selectedActionConfiguration
                    ?.find(e => e.attribute === 'label')
                    ?.mandatory ?? 0;
                return mandatory > 0;
            },
        then: Yup.string().required(requiredFieldError),
        otherwise: Yup.string().notRequired().nullable(),
    }),
});

export const deviceProfileValidationSchema = Yup.object().shape({
    softKeys: Yup.array()
        .of(actionKeyValidator)
        .notRequired(),
    lineKeys: Yup.array()
        .of(actionKeyValidator)
        .notRequired(),
    expansionKeys: Yup.array()
        .of(actionKeyValidator)
        .notRequired(),
    profileName: Yup.string().required(requiredFieldError),
});

export const useDeviceProfileDetailsInitialDate = (isCopy: boolean, profile: OptionsTemplateInfo | null, 
        t: TFunction<string>, uaProfileMetaInfo: GetUAProfileMetaInfoResponse | undefined, 
        phoneDirectories: PhoneDirectory[] | undefined, uaTypes: UATypeInfo[] | undefined) => {
    const softKeysMetadata = uaProfileMetaInfo?.keys_layout?.soft_keys?.keys ?? [];
    const softKeysValues = profile?.keys_options?.filter(e => e.type === 'soft_key') ?? [];
    const softKeysActions = uaProfileMetaInfo?.keys_layout?.soft_keys?.actions ?? [];
        
    const lineKeysMetadata = uaProfileMetaInfo?.keys_layout?.line_keys?.keys ?? [];
    const lineKeysValues = profile?.keys_options?.filter(e => e.type === 'line_key') ?? [];
    const lineKeysActions = uaProfileMetaInfo?.keys_layout?.line_keys?.actions ?? [];

    const expansionKeysMetadata = uaProfileMetaInfo?.keys_layout?.expansion_keys?.keys ?? [];
    const expansionKeysValues = profile?.keys_options?.filter(e => e.type === 'expansion_key') ?? [];
    const expansionKeysActions = uaProfileMetaInfo?.keys_layout?.expansion_keys?.actions ?? [];

    const orderedDirectories: DevicePhoneBookAssignItem[] = [];
    for(const directory of phoneDirectories ?? []) {
        const enabled = !!profile?.directories_options
            ?.find(e => e.i_ua_config_directory === directory.i_ua_config_directory);
        const obj: DevicePhoneBookAssignItem = {
            ...directory,
            enabled: enabled,
            orderIndex: enabled
                ? (profile?.directories_options
                    ?.findIndex(e => e.i_ua_config_directory === directory.i_ua_config_directory) 
                    ?? Number.MAX_SAFE_INTEGER - 1) + 1
                : Number.MAX_SAFE_INTEGER
        }; 
        orderedDirectories.push(obj);
    }
    orderedDirectories.sort((a, b) => a.orderIndex - b.orderIndex || a.name.localeCompare(b.name));
    
    for(const indx in orderedDirectories) {
        orderedDirectories[indx].orderIndex = +indx;
    }

    const obj: DeviceProfileDetailsTabFormType = {
        i_ua_config_template: profile?.i_ua_config_template,
        profileName: isCopy 
            ? (profile?.name ?? '') + ' ' + t('screens:deviceProfiles.copyNameSuf')
            : (profile?.name ?? ''),
        softKeys: mapKeysToArray(softKeysMetadata, softKeysValues, softKeysActions, 'soft_key', profile, uaTypes ?? [], uaProfileMetaInfo?.keys_layout?.soft_keys?.total_keys ?? 1),
        lineKeys: mapKeysToArray(lineKeysMetadata, lineKeysValues, lineKeysActions, 'line_key', profile, uaTypes ?? [], uaProfileMetaInfo?.keys_layout?.line_keys?.total_keys ?? 1),
        expansionKeys: mapKeysToArray(expansionKeysMetadata, expansionKeysValues, expansionKeysActions, 'expansion_key', profile, uaTypes ?? [], uaProfileMetaInfo?.keys_layout?.expansion_keys?.total_keys ?? 1),
        directories: orderedDirectories,
    };
    return obj;
};

export const mapDeviceProfileDetailsTabFormToSaveObject = (_values: DeviceProfileDetailsTabFormType) => {
    const result: TemplateKeysOptionsInfo[] = [];
        
    const pushItem = (type: 'line_key' | 'soft_key' | 'expansion_key', obj: DeviceProfileDetailsActionKeyRowData) => {
        
        let extras = {} as OptionsTemplateExtras;
        try
        {
            extras = obj?.extrasOriginal
                ? JSON.parse(obj?.extrasOriginal)
                : {};
        }
        catch {
        }

        extras.line = obj.line;

        result.push({
            id: obj.keyId + 1,
            type: type,
            label: obj.label?.length ? obj.label : null,
            value: obj.value?.length ? obj.value : null,
            action: obj.selectedAction,
            extras: obj.line != null
                ? JSON.stringify(extras)
                : null,
        });
    };

    for(const obj of _values.lineKeys) {
        if(obj.selectedAction) {
            pushItem('line_key', obj);
        }
    }
    for(const obj of _values.softKeys) {
        if(obj.selectedAction) {
            pushItem('soft_key', obj);
        }
    }
    for(const obj of _values.expansionKeys) {
        if(obj.selectedAction) {
            pushItem('expansion_key', obj);
        }
    }

    return result;
};

const mapKeysToArray = (keyMetadata: UAProfileKeyInfo[], values: TemplateKeysOptionsInfo[], 
        actions: UAProfileKeyAction[], type: 'line_key' | 'soft_key' | 'expansion_key', 
        profile: OptionsTemplateInfo | null, uaTypes: UATypeInfo[],
        totalKeys: number) => {
    const result: DeviceProfileDetailsActionKeyRowData[] = [];

    if(!keyMetadata.length) return result;
    
    for(let metaObjectIndex = 0; metaObjectIndex < totalKeys; metaObjectIndex++)
    {
        const actionsWithDefault: string[] = ['', ...(actions?.map(e => e.type) ?? [])];
        const metaObject = keyMetadata[metaObjectIndex % keyMetadata.length];
        const value = values?.find(e => e.id === (metaObjectIndex + 1));

        const isLineMandatory = actions
            ?.find(e => e.type === value?.action)
            ?.action_attributes
            ?.find(e => e.attribute === 'line')
            ?.mandatory === 1;

        const defaultLineValue = isLineMandatory ? 0 : null;

        let extras = {} as OptionsTemplateExtras;
        try
        {
            extras = value?.extras
                ? JSON.parse(value?.extras)
                : {};
        }
        catch {
        }

        const ports = uaTypes
            ?.find(e => e.i_ua_type === profile?.i_ua_type)
            ?.ports;

        const keyObject: DeviceProfileDetailsActionKeyRowData = {
            keyId: value ? ((value.id ?? 1) - 1) : (metaObjectIndex),
            actions: actionsWithDefault,
            value: value?.value ?? '',
            label: value?.label ?? '',
            itemIdentifierInternal: id(),
            selectedAction: value?.action,
            position: metaObject.position,
            type: type,
            isDisabled: (value?.allow_override ?? YesNo.No) === YesNo.Yes,
            actionsConfiguration: actions ?? [],
            selectedActionConfiguration: (actions ?? [])?.find(e => e.type === value?.action)
                ?.action_attributes ?? [],
            line: extras?.line ?? defaultLineValue,
            maxLines: (ports ?? 16) + 1,
            extrasOriginal: value?.extras
        };
        result.push(keyObject);
    }
    return result;
};